export const mv = {
  meta: {
    title: 'AI MV Generator',
    desc: 'Create lyrics, music, and a music video from one idea.',
  },
  hero: {
    kicker: 'AI music video maker',
    title: 'Turn one idea into lyrics, music, and an MV',
    desc: 'A guided workflow for beginners: describe the song, refine editable lyrics, generate music, then upload images to create a video draft.',
    tags: ['One-sentence prompt', 'Editable lyrics', 'Music preview', 'Lyrics-to-MV'],
    cardTitle: 'Editable chorus',
    cardText: 'Every line can be reviewed before the music and MV are generated.',
  },
  steps: { lyrics: 'Lyrics', music: 'Music', video: 'MV' },
  flow: {
    available: 'Available',
    current: 'Current',
    done: 'Done',
    locked: 'Locked',
    stepOf: 'Step {current} of {total}',
    lyrics: {
      title: 'Create lyrics first',
      desc: 'Start with one sentence. The generated lyrics become the foundation for music and MV creation.',
    },
    music: {
      title: 'Generate music from confirmed lyrics',
      desc: 'Use the lyrics to shape style, vocal, tempo, and a custom music direction.',
    },
    video: {
      title: 'Create the MV after music is ready',
      desc: 'Upload images and describe the visual direction to turn the song into an MV draft.',
    },
  },
  summary: {
    lyricsTitle: 'Confirmed lyrics',
    musicTitle: 'Generated music',
    editLyrics: 'Edit lyrics',
    editMusic: 'Edit music',
  },
  generator: {
    lyrics: {
      idea: 'Describe the song',
      placeholder: 'Example: a warm Chinese song about a summer graduation trip',
      genre: 'Music style',
      mood: 'Mood',
      vocal: 'Vocal',
      language: 'Language',
      length: 'Length',
      settings: 'Lyrics settings',
      button: 'Generate lyrics',
    },
    music: {
      prompt: 'Describe the music',
      placeholder: 'Example: uplifting pop with a clean chorus, bright guitar, and warm female vocal',
      upload: 'Upload music reference images',
      formats: 'Optional. Upload 1-5 images to generate music from visual mood.',
      selected: '{count} images selected',
      style: 'Music style',
      mood: 'Mood',
      vocal: 'Vocal',
      language: 'Language',
      mode: 'Mode',
      duration: 'Duration',
      settings: 'Music settings',
      button: 'Generate music',
    },
    video: {
      prompt: 'Describe the MV',
      placeholder: 'Example: sunny graduation travel vlog with soft transitions and nostalgic color',
      upload: 'Upload MV images',
      formats: 'PNG, JPG, WebP. Single or multiple images.',
      selected: '{count} images selected',
      ratio: 'Aspect ratio',
      resolution: 'Resolution',
      visual: 'Visual style',
      settings: 'MV settings',
      button: 'Generate MV',
    },
  },
  preview: {
    emptyLyricsTitle: 'No Lyrics Yet',
    emptyLyrics: 'Generated lyrics will appear here.',
    emptyLyricsHint: 'Describe the song, then generate editable structured lyrics',
    emptyMusicTitle: 'No Music Yet',
    emptyMusic: 'Generate music after the lyrics are ready.',
    emptyMusicHint: 'Supports style, vocal, tempo, and custom music prompts',
    emptyMvTitle: 'No MV Yet',
    emptyMv: 'Upload images and generate the final MV draft.',
    emptyMvHint: 'Supports single-image, multi-image, aspect ratio, and visual style control',
    lyricsTitle: 'Editable lyrics',
    lyricsHint: 'Review, copy, edit, insert, or delete lines.',
    lyricsReady: 'Lyrics are ready',
    lyricsNext: 'Review the lyrics, then use this version to generate music.',
    musicTitle: 'Music preview',
    musicPlaceholder: 'Audio preview will be available here.',
    musicReady: 'Music is ready',
    musicNext: 'Review the music, then use this version to create the MV.',
    mvTitle: 'MV preview',
    mvPlaceholder: 'Video preview will be available here.',
    sourceImages: 'Source images',
    lyricsBackdrop: 'Synchronized lyrics',
  },
  actions: {
    copy: 'Copy lyrics',
    copySuccess: 'Lyrics copied',
    copyFailed: 'Copy failed',
    edit: 'Edit lyrics',
    save: 'Save lyrics',
    download: 'Download',
    downloadFailed: 'Download failed',
    play: 'Play',
    pause: 'Pause',
    volume: 'Vol',
    audioLoading: 'Loading audio...',
    audioLoadFailed: 'Audio failed to load',
    regenerate: 'Regenerate',
    regenerateWithCredits: 'Regenerate ({count} credits)',
    generateWithCredits: '{label} ({count} credits)',
    generatingWithCredits: 'Generating {progress}% ({count} credits)',
    expand: 'Expand',
    collapse: 'Collapse',
    selectSettings: 'Please select options',
    nextMusic: 'Use these lyrics & continue',
    nextMv: 'Use this music & continue',
  },
  examples: {
    title: 'AI MV Examples for Songs, Stories, and Campaigns',
    sub: 'Explore how one idea can become structured lyrics, a music direction, and an image-led music video draft.',
    items: [
      { badge: 'TRIP', title: 'Graduation travel', text: 'Warm lyrics, pop melody, and a photo-based memory MV.' },
      { badge: 'LOVE', title: 'Anniversary song', text: 'Romantic words, soft vocals, and cinematic image transitions.' },
      { badge: 'BRAND', title: 'Product theme song', text: 'A catchy chorus and branded visuals for short video campaigns.' },
    ],
  },
  howto: {
    title: 'How to Create a Complete AI Music Video',
    sub: 'Create an MV in three guided steps: generate editable lyrics, shape the music, then upload images for a polished video draft.',
    items: [
      { title: 'Generate lyrics', text: 'Enter one sentence, choose mood and language, then edit the structured lyrics.' },
      { title: 'Generate music', text: 'Pick music style, vocal, and tempo. The generated music is used by default.' },
      { title: 'Create MV', text: 'Upload one or more images, set size and style, then generate the MV draft.' },
    ],
  },
  ask: {
    title: 'AI MV Generator - Frequently Asked Questions',
    sub: 'Get answers about lyric editing, music generation, image uploads, regeneration, and download-ready MV results.',
    items: [
      { q: 'Can beginners use it?', a: 'Yes. A single sentence is enough; every step gives a clear next action.' },
      { q: 'Can I edit lyrics?', a: 'Yes. You can copy, edit, insert, and delete lyric lines before generating music.' },
      { q: 'Can I regenerate?', a: 'Yes. Each step can be regenerated while keeping the rest of the flow visible.' },
    ],
  },
};
